import {
  BalanceJson,
  I18n,
  I18nLocale,
  Icon,
  IndexKey,
  PartKey,
  UserId,
  Uuid,
} from '../../models/Base';
import { RepositoryElement } from '../../models/Repository';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';

import { GameType } from './Game';

export type I18nPartInfo = I18n<{ name: string; description?: string }>;

export enum AssistedGameMode {
  Before = 'before',
  After = 'after',
  Off = 'off',
}

export interface GameCollectionGame {
  gameId: Uuid;
  hash: string;
  defaultLocale: I18nLocale;
  gameType: GameType;
  answerSlots: number;
  assistedGame: AssistedGameMode;
  isForChallenge: boolean;
}

export interface GameCollectionSlideshow {
  slideshowId: Uuid;
  hash: string;
  defaultLocale: I18nLocale;
}

export type GameCollectionItem =
  | {
      game: GameCollectionGame;
      slideshow?: never;
      unlockFee: BalanceJson;
      gameReward: BalanceJson;
      slideshowReward?: never;
    }
  | {
      game?: never;
      slideshow: GameCollectionSlideshow;
      unlockFee: BalanceJson;
      gameReward?: never;
      slideshowReward: BalanceJson;
    }
  | {
      game: GameCollectionGame;
      slideshow: 'auto' | GameCollectionSlideshow;
      unlockFee: BalanceJson;
      gameReward: BalanceJson;
      slideshowReward: BalanceJson;
    };
// export type GameCollectionItemWithId = GameCollectionItem & {
//   gameCollectionId: string;
// };
export type GameCollectionPartItem = GameCollectionItem & {
  gameCollectionId: string;
  partId: PartKey;
  indexId: IndexKey;
  icon?: Icon;
};

type GameCollectionPart = {
  i18nPartInfo: I18nPartInfo;
  icon?: Icon;
} & Partial<Record<IndexKey, GameCollectionItem>>;

type StatisticsKey = 'likeCount';

export interface GameCollectionJson<T = number> {
  readonly gameCollectionId: Uuid;
  classroomId: Uuid;
  createdBy: UserId;
  createdAt: T;
  lastModifiedAt: T;
  lastCheckedDependencyAt: T;
  parts: Partial<Record<PartKey, GameCollectionPart>>;
  statistics: Partial<Record<StatisticsKey, number>>;
}

export class GameCollection
  extends RepositoryElement
  implements GameCollectionJson<Date>
{
  readonly gameCollectionId!: Uuid;
  classroomId!: Uuid;
  createdBy!: UserId;
  createdAt!: Date;
  lastModifiedAt!: Date;
  lastCheckedDependencyAt!: Date;
  parts!: Record<PartKey, GameCollectionPart>;
  statistics!: Partial<Record<StatisticsKey, number>>;

  constructor(gameCollection: GameCollectionJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(gameCollection));
  }

  id(): string {
    return this.gameCollectionId;
  }

  toJSON(): GameCollectionJson {
    return deepTransformDate(this) as unknown as GameCollectionJson;
  }

  getPartItemMap<S extends GameCollectionPartItem>(
    ...filters: ((
      value: GameCollectionPartItem,
      index: number,
      array: GameCollectionPartItem[],
    ) => unknown)[]
  ): Record<string, S[]> {
    return Object.entries(this.parts).reduce<Record<string, S[]>>(
      (acc, [partIndex, part]) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { i18nPartInfo, icon, ...parts } = part;
        acc[partIndex] = filters.reduce(
          (items, f) => items.filter(f),
          Object.entries(parts).map(([itemIndex, item]) => ({
            gameCollectionId: this.gameCollectionId,
            partId: partIndex as PartKey,
            indexId: itemIndex as IndexKey,
            icon,
            ...item,
          })),
        ) as S[];
        return acc;
      },
      {},
    );
  }

  getPartItemArray<S extends GameCollectionPartItem>(
    ...filters: ((
      value: GameCollectionPartItem,
      index: number,
      array: GameCollectionPartItem[],
    ) => unknown)[]
  ): S[] {
    const partItems = Object.entries(this.parts).reduce<S[]>(
      (acc, [partIndex, part]) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { i18nPartInfo, icon, ...parts } = part;
        acc.push(
          ...(filters.reduce(
            (items, f) => items.filter(f),
            Object.entries(parts).map(([itemIndex, item]) => ({
              gameCollectionId: this.gameCollectionId,
              partId: partIndex as PartKey,
              indexId: itemIndex as IndexKey,
              icon,
              ...item,
            })),
          ) as S[]),
        );
        return acc;
      },
      [],
    );
    return partItems.sort((a, b) => {
      if (a.indexId !== b.indexId) {
        return parseInt(a.indexId) - parseInt(b.indexId); // Sort by indexId first
      } else {
        return parseInt(a.partId) - parseInt(b.partId); // Then sort by partId
      }
    });
  }

  getGameCollectionItem(id: string): GameCollectionPartItem | undefined {
    let gameCollectionItem;
    Object.entries(this.parts).find(([partIndex, part]) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { i18nPartInfo, icon, ...parts } = part;
      return !!Object.values(parts).find((item) => {
        if (
          item.game?.gameId === id ||
          (item.slideshow !== 'auto' && item.slideshow?.slideshowId === id)
        ) {
          gameCollectionItem = {
            gameCollectionId: this.gameCollectionId,
            partId: partIndex as PartKey,
            icon,
            ...item,
          };
          return true;
        }
        return false;
      });
    });
    return gameCollectionItem;
  }
}
